/**
 * Updates, before the feed arrives.
 *
 * Its own file because two things draw it: the view, while the query runs,
 * and `PortalSkeleton`, while the gate resolves the session. Both were
 * showing something else — the view printed the words "Loading updates…" and
 * the gate drew a heading over one grey block, which is a promise about a
 * layout that is not coming.
 *
 * MEASURED OFF THE REAL SCREEN, not sketched to look like it, the same rule
 * `ScriptReviewSkeleton` follows. Every bar height here is a type role's
 * computed line box: display 28/1.1 is 31, lede 17/1.5 is 26, section 20/1.2
 * is 24, body and strong 14/1.5 are 21, the rail is 10. The avatar is
 * `size-10`, the badges are the 22 the DS pill is fixed at, and the action
 * pills are `h-nf-ds-control-sm`. A skeleton whose blocks land where the
 * content will not is worse than a spinner, because the page moves when it
 * resolves.
 *
 * THE STRUCTURE IS THE REAL STRUCTURE, not an approximation of it. The
 * padding, radii and gaps are the card's own classes rather than numbers that
 * happen to look similar, so the two cannot drift: if the card's padding
 * changes, this changes with it.
 *
 * THREE CARDS, AND NO PINNED BANNER. Three is enough to read as a feed and
 * short enough not to imply a count. The banner is left out for the opposite
 * reason — it is on at most one card and only sometimes, so drawing it would
 * promise a pinned update that may not exist.
 */

import { Skeleton } from "@/components/ui/design-system/skeleton";
import { useCreatorTranslations } from "@/i18n/creator/useCreatorTranslations";
import { cn } from "@/lib/utils";

/** The label widths, from the strings the real header renders. */
const TITLE_W = 112;
const SUBTITLE_W = 296;

export function UpdatesSkeleton({
  /**
   * Cards only, no header.
   *
   * The view already has its real `PageHeader` on screen by the time the
   * query is in flight — the title and the email toggle do not depend on the
   * response. Drawing a skeleton of them there would replace real content
   * with a grey copy of itself, which is a regression, not a loading state.
   * `PortalSkeleton` is the caller that needs the whole page, because at that
   * point nothing is mounted yet.
   */
  listOnly = false,
}: {
  listOnly?: boolean;
} = {}) {
  const t = useCreatorTranslations("updates");
  return (
    <div
      className={cn(
        "flex w-full flex-1 flex-col space-y-nf-ds-6",
        !listOnly && "mx-auto max-w-[1120px] pt-nf-ds-4 lg:py-nf-ds-6",
      )}
    >
      {/* Not `<output>`, see the note in `HomeSkeleton`. */}
      <span className="sr-only">{t("loadingLabel")}</span>

      {listOnly ? null : (
        <>
          {/* `PageHeader`'s own shape: `space-y-4` outside, a `gap-4` row with the
          title column at `space-y-1` and the actions held to their width. */}
          <div className="space-y-4">
            <div className="flex flex-col justify-between gap-4 sm:flex-row sm:items-center">
              <div className="min-w-0 flex-1 space-y-1">
                <Skeleton className="h-[31px]" style={{ width: TITLE_W }} />
                <Skeleton
                  className="h-[26px] max-w-full"
                  style={{ width: SUBTITLE_W }}
                />
              </div>
              {/* The email settings card, which only sits up here at desk width.
              72 is its 40-point disc plus 16 of padding either side. */}
              <Skeleton className="hidden h-[72px] w-[290px] shrink-0 rounded-nf-ds-card lg:block" />
            </div>
          </div>
        </>
      )}

      <div className="space-y-nf-ds-6">
        {[0, 1, 2].map((i) => (
          <UpdateCardSkeleton key={i} index={i} />
        ))}
      </div>
    </div>
  );
}

/**
 * One card.
 *
 * The body lines vary per card so the feed does not read as three copies of
 * one thing — real updates are not the same length, and a column of identical
 * bars is the tell that makes a skeleton look like a loading graphic rather
 * than the page arriving.
 */
function UpdateCardSkeleton({ index }: { index: number }) {
  const titleW = ["58%", "44%", "66%"][index % 3];
  const lastLineW = ["62%", "38%", "74%"][index % 3];

  return (
    <div className="overflow-hidden rounded-nf-ds-card bg-nf-ds-surface-card shadow-nf-ds-pane">
      <div className="space-y-nf-ds-5 p-nf-ds-5 sm:p-nf-ds-8">
        <div className="flex flex-wrap items-center justify-between gap-nf-ds-3">
          <div className="flex items-center gap-nf-ds-3">
            <Skeleton className="size-10 rounded-full" />
            <div className="space-y-nf-ds-1">
              {/* Author on `Strong`, the org and date under it on `Body`. */}
              <Skeleton className="h-[21px] w-[124px]" />
              <Skeleton className="h-[21px] w-[176px]" />
            </div>
          </div>
          {/* The kind pill, and the unread pill that is there more often than
              not on a feed someone has just opened. */}
          <div className="flex items-center gap-nf-ds-2">
            <Skeleton className="h-[22px] w-[104px] rounded-full" />
            <Skeleton className="h-[22px] w-[68px] rounded-full" />
          </div>
        </div>

        <div className="max-w-[72ch] space-y-nf-ds-3">
          <Skeleton className="h-[24px]" style={{ width: titleW }} />
          <div className="space-y-nf-ds-2">
            <Skeleton className="h-[21px] w-full" />
            <Skeleton className="h-[21px] w-[94%]" />
            <Skeleton className="h-[21px]" style={{ width: lastLineW }} />
          </div>
        </div>
      </div>

      {/* The action row, right-aligned like the real one. Widths are the
          measured pills: 125 for "Mark as read", 138 for "Reply privately",
          150 for "Submit a video". The last is only on briefs and bounties,
          so it is on the first card alone rather than all three. */}
      <div className="flex flex-wrap items-center justify-end gap-nf-ds-2 border-t border-nf-ds-border px-nf-ds-5 py-nf-ds-4 sm:px-nf-ds-8">
        <Skeleton className="h-nf-ds-control-sm w-[125px] rounded-nf-ds-button" />
        <Skeleton className="h-nf-ds-control-sm w-[138px] rounded-nf-ds-button" />
        <Skeleton
          className={cn(
            "h-nf-ds-control-sm w-[150px] rounded-nf-ds-button",
            index !== 0 && "hidden",
          )}
        />
      </div>
    </div>
  );
}
